/**
 * @fileoverview Acciones Redux para la gestión de conversaciones en el inbox de Mercately.
 * Incluye funciones para cargar, actualizar y manipular el estado de las conversaciones y sus mensajes.
 * @module actions/inbox/conversationActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */
// PATRÓN REDUX TRADICIONAL: Actions manejan llamadas al servidor
import ConversationModelMC from '../../presentation/models/inbox/ConversationModelMC';
import { getPlatformRetailersStatusForInbox } from './platformRetailersStatusActions';
import { updateOrInsertConversationOptimized } from './conversationOptimizedActions';
import { updateOrAddMessageOptimized } from './messagesOptimizedActions';
import ChatActionsModelMC from '../../presentation/models/inbox/ChatActionsModelMC';
import handleChangeCustomerAgent from '../../services/inbox/changeCustomerAgentService';
import handleChatReadState from '../../services/inbox/chatReadStateService';
import handleChatMessagingState from '../../services/inbox/changeChatMessagingStateService';
import { fetchAndMergeDualApiChats } from '../../services/inbox/dualApiMergeService';
import {
  isInboxMsChat,
  INBOX_MS_API_ALLOWED_PLATFORMS,
  toSidebarPlatformKey
} from '../../services/inbox/inboxMsChatPlatform';
import { toggleChatBotMsInbox } from '../../services/inbox/msInboxToggleChatBotClient';
import { toggleMiaChatBotMsInbox } from '../../services/inbox/msInboxToggleMiaChatbotClient';
import { fetchMsInboxCounters, msInboxCountersRevision } from '../../services/inbox/msInboxCountersClient';
import { hasMonolithCounterPayload, mergePlatformCounts } from '../../services/inbox/dualCountersMergeService';
import { setPlatformCounts } from './unreadChatsActions';
import { toggleMiaChatbot as toggleMiaChatbotLegacy } from '../customers';

// Constantes de acción
export const FETCH_CONVERSATIONS_REQUEST = 'FETCH_CONVERSATIONS_REQUEST';
export const FETCH_CONVERSATIONS_SUCCESS = 'FETCH_CONVERSATIONS_SUCCESS';
export const FETCH_CONVERSATIONS_FAILURE = 'FETCH_CONVERSATIONS_FAILURE';
export const SET_SELECTED_CONVERSATION = 'SET_SELECTED_CONVERSATION';
export const CONVERSATION_MESSAGES_LOADING = 'CONVERSATION_MESSAGES_LOADING';
export const SET_CONVERSATION_MESSAGES_DATA = 'SET_CONVERSATION_MESSAGES_DATA';
export const CONVERSATION_MESSAGES_FAILURE = 'CONVERSATION_MESSAGES_FAILURE';
export const SET_CONVERSATIONS_CURRENT_PAGE = 'SET_CONVERSATIONS_CURRENT_PAGE';
export const SET_CONVERSATION_MESSAGES_CURRENT_PAGE = 'SET_CONVERSATION_MESSAGES_CURRENT_PAGE';
export const SET_EXTERNAL_CONVERSATION = 'SET_EXTERNAL_CONVERSATION';

export const LOAD_DATA_FAILURE = 'LOAD_DATA_FAILURE';

// Unread chats
export const FETCH_UNREAD_CHATS_REQUEST = 'FETCH_UNREAD_CHATS_REQUEST';
export const FETCH_UNREAD_CHATS_SUCCESS = 'FETCH_UNREAD_CHATS_SUCCESS';
export const FETCH_UNREAD_CHATS_FAILURE = 'FETCH_UNREAD_CHATS_FAILURE';

// active filters
export const SET_ACTIVE_INBOX_FILTER = 'SET_ACTIVE_INBOX_FILTER';
export const SET_ACTIVE_INBOX_SORT = 'SET_ACTIVE_INBOX_SORT';

/**
 * Selecciona una conversación por su ID.
 * Incluye `currentUserId` en el payload para que el reducer pueda reevaluar filtros
 * al cambiar de chat (p. ej. quitar de la lista la anterior con filtros de actividad).
 *
 * @function setSelectedConversation
 * @memberof module:actions/inbox/conversationActions
 * @param {string|number|null} conversationId - ID de la conversación a seleccionar.
 * @returns {Function} Thunk que despacha la acción Redux.
 * @example
 * dispatch(setSelectedConversation(123));
 */
export const setSelectedConversation = (conversationId) => (dispatch, getState) => {
  const currentUserId = getState().mainReducer?.currentRetailerUser?.id;
  dispatch({
    type: SET_SELECTED_CONVERSATION,
    payload: { conversationId, currentUserId }
  });
};

/**
 * Guarda la conversación abierta desde fuera de la lista (p. ej. otro canal del
 * mismo customer). `conversationsReducer.data` está filtrado por canal y paginado,
 * así que esas conversaciones no viven ahí; `useSelectedConversation` cae a este
 * slot cuando el id seleccionado no está en la lista.
 *
 * @function setExternalConversation
 * @memberof module:actions/inbox/conversationActions
 * @param {Object|null} conversation - Item crudo de la API `{ id, type, attributes }`.
 * @returns {Object} Acción Redux.
 */
export const setExternalConversation = (conversation) => ({
  type: SET_EXTERNAL_CONVERSATION,
  payload: conversation
});

/**
 * Obtiene conversaciones filtradas según los parámetros indicados.
 */
export const fetchConversationById = ({ conversationId, platform }) => async (dispatch) => {
  dispatch({ type: FETCH_CONVERSATIONS_REQUEST });
  try {
    const { chat, found } = await ConversationModelMC.getConversationById({ conversationId, platform });
    if (!found) return;
    dispatch({ type: FETCH_CONVERSATIONS_SUCCESS, payload: { data: [chat] } });
  } catch (error) {
    dispatch({ type: FETCH_CONVERSATIONS_FAILURE, error: error.message || "An unexpected error occurred." });
  }
};

/**
 * Obtiene conversaciones filtradas según los parámetros indicados.
 * Mezcla resultados de la API principal (Mercately) y el microservicio inbox cuando aplica,
 * delegando la lógica de merge al servicio dualApiMergeService.
 */
export const fetchConversationsByFilter = (params) => async (dispatch, getState) => {
  const page = params.page || 1;
  dispatch({ type: FETCH_CONVERSATIONS_REQUEST, meta: { page } });
  try {
    const { pagination } = getState().conversationsReducer || {};
    const finalData = await fetchAndMergeDualApiChats(params, pagination);
    dispatch({ type: FETCH_CONVERSATIONS_SUCCESS, payload: finalData });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("fetchConversationsByFilter error:", error);
    dispatch({ type: FETCH_CONVERSATIONS_FAILURE, error: error.message || "An unexpected error occurred." });
  }
};

/**
 * Obtiene los mensajes de una conversación específica, ordenados por fecha de creación.
 */
export const fetchConversationMessages = (conversationId, options = {}) => async (dispatch) => {
  const { platform, page = 1 } = options;

  dispatch({ type: CONVERSATION_MESSAGES_LOADING });
  try {
    if (INBOX_MS_API_ALLOWED_PLATFORMS.has(platform)) {
      const data = await ConversationModelMC.getMicroserviceMessages({
        id: conversationId,
        page,
        platform
      });
      data.data = data.data
        .map((msg) => ({ ...msg, _createdTime: Date.parse(msg.created_time) }))
        .sort((a, b) => a._createdTime - b._createdTime)
        .map(({ _createdTime, ...msg }) => msg);
      dispatch({ type: SET_CONVERSATION_MESSAGES_DATA, payload: data });
      return data.data;
    }

    const data = await ConversationModelMC.getMessages({ id: conversationId, platform, page });

    data.data = data.data
      .map((msg) => ({ ...msg, _createdTime: Date.parse(msg.created_time) }))
      .sort((a, b) => a._createdTime - b._createdTime)
      .map(({ _createdTime, ...msg }) => msg);
    dispatch({ type: SET_CONVERSATION_MESSAGES_DATA, payload: data });
    return data.data;
  } catch (error) {
    dispatch({ type: CONVERSATION_MESSAGES_FAILURE, error });
    return [];
  }
};

/**
 * Cambia el estado de lectura de un chat (leído/no leído) y sincroniza con el servidor usando un servicio reutilizable.
 */
export const setChatReadToggleState = (body, options = {}) => async (dispatch) => handleChatReadState(dispatch, body, options);

/**
 * Cambia el estado de mensajería de un chat (status_chat) y sincroniza con el servidor usando un servicio reutilizable.
 */
export const changeChatMessagingState = (body, prevStatusChat) => async (dispatch) => handleChatMessagingState(dispatch, body, prevStatusChat);

/**
 * Bloquea o desbloquea un usuario en una conversación y sincroniza con el servidor.
 * Realiza actualización local optimista y rollback en caso de error.
 */
export const toggleBlockUser = (conversationId, body, prevBlocked) => async (dispatch) => {
  const { id } = body || {};
  if (!conversationId || !id) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: 'Missing data to toggle block user'
    });
    return { success: false, error: 'Missing data to toggle block user' };
  }

  // 1. Actualizar estado local
  const updateResult = await dispatch(updateOrInsertConversationOptimized({
    id: conversationId,
    changes: { blocked: !prevBlocked },
    isUpsert: true
  }));

  if (!updateResult.success) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: updateResult.error || 'Error updating conversation state'
    });
    return { success: false, error: updateResult.error };
  }

  // 2. Enviar al servidor
  try {
    await ChatActionsModelMC.toggleBlockUser({ id });
    return { success: true };
  } catch (error) {
    // 3. Rollback
    await dispatch(updateOrInsertConversationOptimized({
      id: conversationId,
      changes: { blocked: prevBlocked },
      isUpsert: false
    }));

    dispatch({
      type: LOAD_DATA_FAILURE,
      error: error.message || 'Error blocking user'
    });
    return { success: false, error: error.message };
  }
};

/**
 * Activa o desactiva el flujo de bot en una conversación y sincroniza con el servidor.
 * Realiza actualización local optimista y rollback en caso de error.
 */
export const toggleChatFlow = (
  conversationId,
  body,
  currentHasAllowFlow,
  currentHasActiveBot = false
) => async (dispatch) => {
  const { id, chat_service: platform } = body || {};
  if (!conversationId || !id || !platform) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: 'Missing data to toggle chat flow'
    });
    return { success: false, error: 'Missing data to toggle chat flow' };
  }

  const nextHasAllowFlow = !currentHasAllowFlow;
  const optimisticFlowChanges = {
    allow_start_bots: nextHasAllowFlow
  };
  if (!nextHasAllowFlow) optimisticFlowChanges.active_bot = false;

  // 1. Actualizar estado local
  const updateResult = await dispatch(updateOrInsertConversationOptimized({
    id: conversationId,
    changes: optimisticFlowChanges
  }));

  if (!updateResult.success) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: updateResult.error || 'Error updating conversation bot state'
    });
    return { success: false, error: updateResult.error };
  }

  // 2. Enviar al servidor
  try {
    if (isInboxMsChat(platform)) {
      // Plataformas servidas por el microservicio (TikTok / Messenger): el bot
      // se maneja en el microservicio de chatbots. El toggle va al inbox MS,
      // que actualiza el chat y notifica al motor de chatbots por Kafka.
      await toggleChatBotMsInbox({
        id: conversationId,
        platform,
        enabled: nextHasAllowFlow
      });
    } else {
      await ChatActionsModelMC.toggleChatBot(body);
    }
    return { success: true };
  } catch (error) {
    // 3. Rollback
    await dispatch(updateOrInsertConversationOptimized({
      id: conversationId,
      changes: {
        allow_start_bots: currentHasAllowFlow,
        active_bot: currentHasActiveBot
      }
    }));

    dispatch({
      type: LOAD_DATA_FAILURE,
      error: error.message || 'Error toggling chat flow'
    });
    return { success: false, error: error.message };
  }
};

/**
 * Activa o desactiva las respuestas automáticas de MIA en una conversación y
 * sincroniza con el servidor. Realiza actualización local optimista y
 * rollback en caso de error — mismo patrón que `toggleChatFlow` de arriba.
 *
 * Plataformas ruteadas al microservicio (TikTok siempre; Messenger/Instagram
 * cuando el retailer tiene el rollout flag activo, "multipágina") van al
 * inbox MS, que escribe el estado real en `customer_mia_chatbot.mia_active`
 * (Mongo). El resto usa el endpoint MIA Rails-nativo existente.
 *
 * @param {string|number} conversationId
 * @param {string} csrfToken - Requerido por el endpoint Rails-nativo.
 * @param {{ platform: string }} body - `platform` tal cual viene en
 *   `conversation.platform` (corto para chats Rails-nativos, largo/canónico
 *   para chats ruteados al microservicio).
 * @param {boolean} currentHasMia
 */
export const toggleMiaResponses = (
  conversationId,
  csrfToken,
  body,
  currentHasMia
) => async (dispatch) => {
  const { platform } = body || {};
  if (!conversationId || !platform) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: 'Missing data to toggle MIA responses'
    });
    return { success: false, error: 'Missing data to toggle MIA responses' };
  }

  const nextHasMia = !currentHasMia;

  const updateResult = await dispatch(updateOrInsertConversationOptimized({
    id: conversationId,
    changes: { mia_chatbot_active: nextHasMia }
  }));

  if (!updateResult.success) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: updateResult.error || 'Error updating conversation MIA state'
    });
    return { success: false, error: updateResult.error };
  }

  try {
    if (isInboxMsChat(platform)) {
      await toggleMiaChatBotMsInbox({
        id: conversationId,
        platform,
        enabled: nextHasMia
      });
    } else {
      await dispatch(toggleMiaChatbotLegacy(conversationId, csrfToken, {
        platform: toSidebarPlatformKey(platform)
      }));
    }
    return { success: true, hasMia: nextHasMia };
  } catch (error) {
    await dispatch(updateOrInsertConversationOptimized({
      id: conversationId,
      changes: { mia_chatbot_active: currentHasMia }
    }));

    dispatch({
      type: LOAD_DATA_FAILURE,
      error: error.message || 'Error toggling MIA responses'
    });
    return { success: false, error: error.message };
  }
};

/**
 * Cambia el agente asignado a una conversación y sincroniza con el servidor.
 * Realiza actualización local optimista y rollback en caso de error.
 */
export const changeCustomerAgent = (bodies, options) => async (dispatch, getState) => handleChangeCustomerAgent(dispatch, getState, bodies, options);

/**
 * Recupera las fuentes MIA desde el servidor y actualiza los mensajes correspondientes.
 */
export const fetchRetrieveMiaSources = (params) => async (dispatch) => {
  try {
    const response = await ChatActionsModelMC.retrieveMiaSources(params);

    if (response?.data && typeof response.data === 'object') {
      const miaSources = response.data;
      const messageEntries = Object.entries(miaSources);

      // Para volúmenes grandes, procesar en paralelo en lugar de secuencialmente
      const updatePromises = messageEntries.map(([messageId, miaData]) => dispatch(updateOrAddMessageOptimized({
        id: messageId,
        customer_id: params?.customer_id || null,
        platform: params?.platform || null,
        mia_sources_response: miaData
      })));

      // Ejecutar todas las actualizaciones en paralelo
      await Promise.all(updatePromises);

      return { success: true, processedMessages: messageEntries.length };
    }

    return { success: true, processedMessages: 0 };
  } catch (error) {
    dispatch({
      type: LOAD_DATA_FAILURE,
      error: error.message || 'Error retrieving MIA sources'
    });
    return { success: false, error: error.message || 'Error retrieving MIA sources' };
  }
};

/**
 * Obtiene los chats no leídos y los almacena en el store.
 */
export const getUnreadChats = () => async (dispatch, getState) => {
  dispatch({ type: FETCH_UNREAD_CHATS_REQUEST });
  const revisionAtStart = msInboxCountersRevision();
  try {
    const [monolithData, msCounters] = await Promise.all([
      ConversationModelMC.getUnreadChats(),
      fetchMsInboxCounters(),
      // Warm shared cache; ownership lives in platformRetailersStatusReducer (never null-overwrite).
      dispatch(getPlatformRetailersStatusForInbox())
    ]);
    const retailerInfo = getState().retailerUsersReducer?.retailer_info;
    const platformCounts = mergePlatformCounts(
      monolithData,
      msCounters,
      retailerInfo?.inbox_microservice_platforms,
      retailerInfo?.inbox_microservice_migrating_platforms
    );

    dispatch({
      type: FETCH_UNREAD_CHATS_SUCCESS,
      payload: { unreadChats: monolithData }
    });
    // Un delta que llegó mientras el GET iba en vuelo ya es más nuevo que este snapshot.
    if (msInboxCountersRevision() === revisionAtStart) {
      dispatch(setPlatformCounts(platformCounts));
    }

    return { success: true, data: monolithData, platformCounts };
  } catch (error) {
    dispatch({
      type: FETCH_UNREAD_CHATS_FAILURE,
      error: error.message || 'Error fetching unread chats'
    });
    return { success: false, error: error.message };
  }
};

/**
 * Re-merge contadores MS con arrays monolito actuales (reconciliación socket).
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Pide `/counters` de nuevo. La reconciliación
 *        de `chat_counter_update` lo usa: el caché de startup tiene el snapshot anterior.
 */
export const refreshMergedPlatformCounts = ({ force = false } = {}) => async (dispatch, getState) => {
  // Sin los arrays del monolito no hay base para mergear: `mergePlatformCounts(null, ...)`
  // devolvería todos los canales en cero y pisaría un valor válido. `{}` tampoco cuenta.
  // `getUnreadChats` hará el merge cuando termine de cargar las dos fuentes.
  if (!hasMonolithCounterPayload(getState().unreadChatsReducer?.data)) return;

  const revisionAtStart = msInboxCountersRevision();
  const msCounters = await fetchMsInboxCounters({ force });
  if (msInboxCountersRevision() !== revisionAtStart) return;

  const currentData = getState().unreadChatsReducer?.data;
  if (!hasMonolithCounterPayload(currentData)) return;

  const retailerInfo = getState().retailerUsersReducer?.retailer_info;
  dispatch(setPlatformCounts(mergePlatformCounts(
    currentData,
    msCounters,
    retailerInfo?.inbox_microservice_platforms,
    retailerInfo?.inbox_microservice_migrating_platforms
  )));
};

/**
 * Establece el filtro activo en la bandeja de entrada.
 */
export const setActiveInboxFilter = (filter) => ({
  type: SET_ACTIVE_INBOX_FILTER,
  payload: filter
});

/**
 * Establece la configuración de orden activo en la bandeja de entrada.
 */
export const setActiveInboxSort = (sortConfig) => ({
  type: SET_ACTIVE_INBOX_SORT,
  payload: sortConfig
});
